// Détection HEIC/HEIF (NutriScan 10, § « HEIC/HEIF — le piège iPhone ») : un iPhone enregistre ses
// photos dans ce format par défaut, et c'est précisément ce que « importer depuis la pellicule »
// vise en premier lieu. Safari iOS sait le décoder dans un canvas ; Chrome et Firefox, sur bureau
// COMME sur Android, ne le savent pas — `web/src/capture/compression.ts` (réutilisé tel quel, cf.
// CLAUDE.md) échoue alors silencieusement à `createImageBitmap`. Ce module ne fait qu'identifier le
// format pour transformer cet échec générique en message compréhensible (import/importRunner.ts) :
// il ne décode rien lui-même.
//
// `File.type` n'est pas fiable pour HEIC : un fichier transféré par messagerie ou exporté depuis les
// Photos arrive parfois avec un type MIME vide, ou générique (`application/octet-stream`) selon le
// système. Les octets de tête (boîte `ftyp` ISO BMFF), eux, ne mentent pas.

/** Marques ("brands") ISO BMFF qui identifient un fichier HEIC/HEIF — image fixe ou séquence. */
const MARQUES_HEIC = new Set(['heic', 'heix', 'heim', 'heis', 'hevc', 'hevx', 'hevm', 'hevs', 'mif1', 'msf1']);

/**
 * Lit la boîte `ftyp` ISO BMFF en tête du fichier (mêmes octets que ceux déjà lus pour l'EXIF,
 * `capture/exifDate.ts` — aucune lecture supplémentaire n'est nécessaire côté appelant).
 * `[taille 4 octets][b'ftyp'][marque majeure 4 octets][version][marques compatibles...]`.
 */
export function estEnteteHeic(entete: Uint8Array): boolean {
  if (entete.length < 12) return false;
  const estFtyp =
    entete[4] === 0x66 && entete[5] === 0x74 && entete[6] === 0x79 && entete[7] === 0x70; // "ftyp"
  if (!estFtyp) return false;
  const marqueMajeure = String.fromCharCode(entete[8]!, entete[9]!, entete[10]!, entete[11]!);
  if (MARQUES_HEIC.has(marqueMajeure)) return true;
  // Certains encodeurs déclarent une marque majeure générique (`mif1`/`msf1` déjà couvertes) et une
  // marque HEIC seulement parmi les « marques compatibles » qui suivent — on les scanne aussi, par
  // blocs de 4 octets, jusqu'à la fin de ce qu'on a lu.
  for (let pos = 16; pos + 4 <= entete.length; pos += 4) {
    const marque = String.fromCharCode(entete[pos]!, entete[pos + 1]!, entete[pos + 2]!, entete[pos + 3]!);
    if (MARQUES_HEIC.has(marque)) return true;
  }
  return false;
}

/** Nom/extension : filet de sécurité si les octets de tête n'ont pas pu être lus (jamais la SEULE
 *  vérification — un fichier renommé en `.jpg` doit rester détecté par ses octets). */
export function nomSuggereHeic(nomFichier: string): boolean {
  return /\.(heic|heif)$/i.test(nomFichier.trim());
}

/** Vrai si tout indique un fichier HEIC/HEIF — octets de tête d'abord, extension en repli. */
export function estProbablementHeic(fichier: { name: string }, entete: Uint8Array): boolean {
  return estEnteteHeic(entete) || nomSuggereHeic(fichier.name);
}
